import React from 'react';
import Header from '../../../components/layout/header/landing';
import useDriverRouteProgress from './hook';

function getDriverDisplayName(first: string | null, last: string | null): string {
  const full = `${first || ''} ${last || ''}`.trim();
  return full || 'Unknown';
}

function getStatusLabel(status: number | null): string {
  if (status === 3) return 'In Route';
  if (status === 4) return 'Finished';
  if (status === 2) return 'Starting';
  if (status == null) return 'Unknown';
  return `Status ${status}`;
}

function getStatusBadgeClasses(status: number | null): string {
  if (status === 3) return 'badge bg-primary-subtle text-primary';
  if (status === 4) return 'badge bg-success-subtle text-success';
  if (status === 2) return 'badge bg-warning-subtle text-warning';
  return 'badge bg-secondary-subtle text-secondary';
}

function formatElapsed(minutes: number | null): string {
  if (minutes == null) return '-';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function RouteDriverProgress(): React.ReactElement {
  const {
    snapshot,
    rows,
    isLoading,
    error,
    refetch,
  } = useDriverRouteProgress();

  const clientsRemaining = Math.max(snapshot.clientsTotal - snapshot.clientsVisited, 0);
  const vmsRemaining = Math.max(snapshot.vmsTotal - snapshot.vmsServiced, 0);

  return (
    <div className="py-4">
      <Header title="Route Driver Progress" />
      <div className="px-4 px-xl-5">
        {error ? (
          <div className="alert alert-danger d-flex justify-content-between align-items-center">
            <div>{error}</div>
            <button
              type="button"
              className="btn btn-sm btn-outline-light"
              onClick={() => {
                refetch().catch(() => {
                  // error state is already handled in hook
                });
              }}
            >
              Retry
            </button>
          </div>
        ) : null}

        {/* Top summary KPIs */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-lg-3">
            <div className="card h-100">
              <div className="card-body py-3">
                <div className="fz-11 text-muted mb-1">Drivers In Route</div>
                <div className="d-flex align-items-baseline">
                  <div className="fz-22 fw-600 me-2">{snapshot.driversInRoute}</div>
                  <div className="fz-11 text-muted">
                    {`of ${snapshot.driversScheduled} scheduled`}
                  </div>
                </div>
                {isLoading ? (
                  <div className="fz-11 text-muted mt-1">Loading…</div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="col-6 col-lg-3">
            <div className="card h-100">
              <div className="card-body py-3">
                <div className="fz-11 text-muted mb-1">Routes In Progress</div>
                <div className="d-flex align-items-baseline">
                  <div className="fz-22 fw-600 me-2">{snapshot.routesInProgress}</div>
                  <div className="fz-11 text-muted">
                    {`${snapshot.routesFinished} finished`}
                  </div>
                </div>
                {isLoading ? (
                  <div className="fz-11 text-muted mt-1">Loading…</div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="col-6 col-lg-3">
            <div className="card h-100">
              <div className="card-body py-3">
                <div className="fz-11 text-muted mb-1">Clients Visited</div>
                <div className="d-flex align-items-baseline">
                  <div className="fz-22 fw-600 me-2">{snapshot.clientsVisited}</div>
                  <div className="fz-11 text-muted">
                    {`of ${snapshot.clientsTotal} total`}
                  </div>
                </div>
                <div className="fz-11 text-muted mt-1">
                  Remaining:
                  {' '}
                  <span className="fw-500">{clientsRemaining}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="col-6 col-lg-3">
            <div className="card h-100">
              <div className="card-body py-3">
                <div className="fz-11 text-muted mb-1">VMs Serviced</div>
                <div className="d-flex align-items-baseline">
                  <div className="fz-22 fw-600 me-2">{snapshot.vmsServiced}</div>
                  <div className="fz-11 text-muted">
                    {`of ${snapshot.vmsTotal} total`}
                  </div>
                </div>
                <div className="fz-11 text-muted mt-1">
                  Remaining:
                  {' '}
                  <span className="fw-500">{vmsRemaining}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fz-16 fw-600">Drivers in Route</div>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  refetch().catch(() => {
                    // error state is already handled in hook
                  });
                }}
                disabled={isLoading}
              >
                {isLoading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>

            <div className="table-responsive">
              <table className="table table-sm mb-0 fz-13">
                <thead>
                  <tr className="text-muted">
                    <th>Route</th>
                    <th>Driver</th>
                    <th>Status</th>
                    <th className="text-end">Clients</th>
                    <th className="text-end">VMs</th>
                    <th className="text-end">Start</th>
                    <th className="text-end">Elapsed</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.routeServiceId}>
                      <td>
                        <div className="fw-500">
                          {r.routeName || `RouteService #${r.routeServiceId}`}
                        </div>
                        {r.routeScheduleId ? (
                          <div className="fz-11 text-muted">
                            {`Schedule #${r.routeScheduleId}`}
                          </div>
                        ) : null}
                      </td>

                      <td>{getDriverDisplayName(r.driverFirstName, r.driverLastName)}</td>

                      <td>
                        <span className={getStatusBadgeClasses(r.routeStatus)}>
                          {getStatusLabel(r.routeStatus)}
                        </span>
                      </td>

                      <td className="text-end">
                        {`${r.clientsVisited ?? 0} / ${r.clientsTotal ?? 0}`}
                      </td>

                      <td className="text-end">
                        {`${r.vmsServiced ?? 0} / ${r.vmsTotal ?? 0}`}
                      </td>

                      <td className="text-end">{r.startTime || '-'}</td>

                      <td className="text-end">
                        {formatElapsed(r.elapsedMinutes)}
                      </td>
                    </tr>
                  ))}

                  {!isLoading && rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-3">
                        No active route services.
                      </td>
                    </tr>
                  ) : null}

                  {isLoading && rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-3">
                        Loading…
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RouteDriverProgress;
